import React, { useEffect, useMemo, useState } from 'react';
import FAQSection from '../FAQSection';
import { helpTopics } from '../common/helpTopics';
import TicketForm from '../forms/TicketForm';
import apiClient from '../../api/api';

const ProfileFaqModal = ({ isOpen, onClose, modalData }) => {
  const [activeTab, setActiveTab] = useState('faq');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [myTickets, setMyTickets] = useState([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);

  const allowedTopics = useMemo(() => {
    const ids = modalData?.categoryIds;
    if (!ids || ids.length === 0) return helpTopics;
    return helpTopics.filter((topic) => topic && ids.includes(topic.id));
  }, [modalData]);

  const filteredTopics = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return allowedTopics;
    return allowedTopics.filter((topic) =>
      topic && (
        topic.title.toLowerCase().includes(term) ||
        topic.description.toLowerCase().includes(term) ||
        topic.questions.some((q) => q.question.toLowerCase().includes(term))
      )
    );
  }, [allowedTopics, searchTerm]);

  const handleBack = () => {
    setSelectedTopic(null);
    setSearchTerm('');
  };

  useEffect(() => {
    const fetchTickets = async () => {
      setIsLoadingTickets(true);
      try {
        const response = await apiClient.get('/support/tickets/my');
        setMyTickets(response.data || []);
      } catch (error) {
        setMyTickets([]);
      } finally {
        setIsLoadingTickets(false);
      }
    };

    if (activeTab === 'my-tickets') {
      fetchTickets();
    }
  }, [activeTab]);

  useEffect(() => {
    setSelectedTopic(null);
    setSearchTerm('');
  }, [activeTab]);

  if (!isOpen) return null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">Preguntas del Perfil</h2>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
          aria-label="Cerrar"
        >
          <i className="fas fa-times"></i>
        </button>
      </div>

      <div className="border-b border-gray-200 mb-4">
        <nav className="flex space-x-4 text-xs font-semibold">
          <button
            onClick={() => setActiveTab('faq')}
            className={`pb-2 ${activeTab === 'faq' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
          >
            Preguntas frecuentes
          </button>
          <button
            onClick={() => setActiveTab('my-tickets')}
            className={`pb-2 ${activeTab === 'my-tickets' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
          >
            Mis preguntas
          </button>
          <button
            onClick={() => setActiveTab('ticket')}
            className={`pb-2 ${activeTab === 'ticket' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`}
          >
            Crear ticket
          </button>
        </nav>
      </div>

      {activeTab === 'faq' && (
        selectedTopic ? (
          <div className="space-y-4">
            <button
              onClick={handleBack}
              className="text-xs font-semibold text-blue-600 hover:underline"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Volver a temas
            </button>
            <FAQSection faqs={{ [selectedTopic.title]: selectedTopic.questions }} />
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                Encuentra respuestas rapidas sobre tu perfil, seguridad y privacidad.
              </p>
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar preguntas..."
                className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {filteredTopics.map((topic) => (
                <button
                  key={topic.id}
                  onClick={() => setSelectedTopic(topic)}
                  className="p-4 border border-gray-200 rounded-lg text-left hover:border-blue-500 hover:shadow-sm transition"
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <topic.icon className="w-5 h-5 text-blue-600" />
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">{topic.title}</h3>
                  </div>
                  <p className="text-xs text-gray-500">{topic.description}</p>
                </button>
              ))}
            </div>

            {filteredTopics.length === 0 && (
              <p className="text-xs text-gray-500">No se encontraron temas.</p>
            )}
          </div>
        )
      )}

      {activeTab === 'my-tickets' && (
        <div className="space-y-3">
          {isLoadingTickets && (
            <p className="text-xs text-gray-500">Cargando tus preguntas...</p>
          )}
          {!isLoadingTickets && myTickets.length === 0 && (
            <p className="text-xs text-gray-500">No tienes preguntas registradas.</p>
          )}
          {!isLoadingTickets && myTickets.length > 0 && (
            <div className="space-y-2">
              {myTickets.map((ticket) => (
                <div key={ticket._id} className="p-3 border rounded-lg">
                  <p className="text-xs font-semibold text-gray-700">{ticket.subject}</p>
                  <p className="text-[10px] text-gray-500">Estado: {ticket.status}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'ticket' && (
        <div className="space-y-3">
          <p className="text-xs text-gray-600">Crea una nueva pregunta para soporte.</p>
          <TicketForm />
        </div>
      )}
    </div>
  );
};

export default ProfileFaqModal;
